/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css, html } from 'lit';
import { SHARED_STYLES } from '../css/shared-css.js';
import { CREATEABLE_STAGES, FORMS_BY_STAGE_TYPE } from './form-definition.js';
import { customElement, property, query } from 'lit/decorators.js';
let addStageDialogEl;
let currentFeatureId;
export async function openAddStageDialog(featureId, featureType, onSubmitCustomHandler) {
    if (!addStageDialogEl ||
        currentFeatureId !== featureId ||
        onSubmitCustomHandler !== addStageDialogEl.onSubmitCustomHandler) {
        addStageDialogEl = document.createElement('chromedash-add-stage-dialog');
        addStageDialogEl.featureId = featureId;
        addStageDialogEl.featureType = featureType;
        addStageDialogEl.onSubmitCustomHandler = onSubmitCustomHandler;
        document.body.appendChild(addStageDialogEl);
        await addStageDialogEl.updateComplete;
    }
    currentFeatureId = featureId;
    addStageDialogEl.show();
}
let ChromedashAddStageDialog = class ChromedashAddStageDialog extends LitElement {
    constructor() {
        super(...arguments);
        this.featureId = 0;
        this.featureType = 0;
        this.canSubmit = false;
        this.onSubmitCustomHandler = null;
    }
    static get styles() {
        return [
            ...SHARED_STYLES,
            css `
        #controls {
          padding: var(--content-padding);
          text-align: right;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        #controls * + * {
          padding-left: var(--content-padding);
        }
      `,
        ];
    }
    show() {
        this.dialog.show();
    }
    renderSelectMenuItems() {
        const menuItems = [];
        for (const stageType of CREATEABLE_STAGES[this.featureType]) {
            // Get the name of the stage from the form definition based on the stage type.
            const stageInfo = FORMS_BY_STAGE_TYPE[stageType];
            menuItems.push(html `
        <sl-option value="${stageType}"> ${stageInfo.name} </sl-option>
      `);
        }
        return menuItems;
    }
    getStageSelectValue() {
        return this.stageCreateSelect.value;
    }
    handleStageCreate() {
        if (this.onSubmitCustomHandler) {
            this.onSubmitCustomHandler({
                stage_type: Number(this.getStageSelectValue()),
            });
            this.onSubmitCustomHandler = null;
            this.dialog.hide();
            return;
        }
        window.csClient
            .createStage(this.featureId, {
            stage_type: {
                form_field_name: 'stage_type',
                value: this.getStageSelectValue(),
            },
        })
            .then(() => {
            this.dialog.hide();
            location.reload();
        });
    }
    checkCanSubmit() {
        this.canSubmit = this.getStageSelectValue() !== 0;
    }
    renderStageSelect() {
        return html `
      <div id="controls">
        <sl-select
          placement="top"
          hoist
          value="0"
          id="stage_create_select"
          size="small"
          @sl-change=${this.checkCanSubmit}
          style="width:16rem"
        >
          <sl-option value="0" disabled>Select a stage to create</sl-option>
          ${this.renderSelectMenuItems()}
        </sl-select>
        <sl-button
          variant="primary"
          @click=${this.handleStageCreate}
          ?disabled=${!this.canSubmit}
          size="small"
          >Create stage</sl-button
        >
      </div>
    `;
    }
    render() {
        return html `
      <sl-dialog label="Create a new stage">
        <p>Here, you can add additional stages to your feature as needed.</p>
        ${this.renderStageSelect()}
      </sl-dialog>
    `;
    }
};
__decorate([
    property({ type: Number })
], ChromedashAddStageDialog.prototype, "featureId", void 0);
__decorate([
    property({ type: Number })
], ChromedashAddStageDialog.prototype, "featureType", void 0);
__decorate([
    property({ type: Boolean })
], ChromedashAddStageDialog.prototype, "canSubmit", void 0);
__decorate([
    property({ attribute: false })
], ChromedashAddStageDialog.prototype, "onSubmitCustomHandler", void 0);
__decorate([
    query('#stage_create_select')
], ChromedashAddStageDialog.prototype, "stageCreateSelect", void 0);
__decorate([
    query('sl-dialog')
], ChromedashAddStageDialog.prototype, "dialog", void 0);
ChromedashAddStageDialog = __decorate([
    customElement('chromedash-add-stage-dialog')
], ChromedashAddStageDialog);
